/** Produkttabelle — reine Darstellung, alle Aktionen kommen als Callback herein. */
import { eur } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Th, Td, SortTh } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2, Archive } from "lucide-react";
import { dimStr } from "./ProductFields";

const dimClass = (v) => {
  if (v.is_digital) return "text-muted-foreground";
  return dimStr(v) === "—" ? "text-chart-3" : "";
};

export default function ProductsTable({ rows, supMap, canEdit, canCost, sortKey, sortDir, onSort,
  onEdit, onArchive, onDelete }) {
  const { t } = useI18n();
  const sortTh = (id, label, right) => (
    <SortTh sticky sortId={id} right={right} active={sortKey === id} dir={sortDir} onSort={onSort}>{label}</SortTh>
  );
  return (
    <div className="flex-1 min-h-0 overflow-auto rounded-md border border-border bg-card shadow-sm">
      <table className="w-full">
        <thead className="bg-muted/50"><tr>
          {sortTh("name", t("prod.nameSku"))}
          <Th sticky>{t("prod.dimensions")}</Th>
          {sortTh("supplier", t("common.supplier"))}
          {sortTh("ean", "EAN")}
          {sortTh("hs", t("prod.hsCode"))}
          {canCost && sortTh("cost", t("common.costPrice"), true)}
          {sortTh("net", t("common.net"), true)}
          <Th sticky right>{t("common.gross")}</Th>
          <Th sticky right title={t("prod.secondChoice")}>{t("prod.secondNetShort")}</Th>
          <Th sticky right title={t("prod.secondChoice")}>{t("prod.secondGrossShort")}</Th>
          <Th sticky></Th>
        </tr></thead>
        <tbody data-testid="products-table">
          {rows.map((p) => {
            const v = p.variants?.[0] || {};
            return (
              <tr key={p.id} className={`transition-colors ${p.archived ? "bg-muted/40 opacity-70 hover:opacity-100" : "hover:bg-muted/40"}`}>
                <Td>
                  <div className="font-medium text-foreground">{p.name}
                    {p.archived && <span className="ml-1.5 rounded-full bg-muted-foreground/20 text-muted-foreground text-[10px] px-1.5 py-0.5 uppercase tracking-wide">{t("common.archived")}</span>}
                    {v.is_digital && <span className="ml-1.5 rounded-full bg-chart-4/15 text-chart-4 text-[10px] px-1.5 py-0.5">{t("common.digital")}</span>}
                  </div>
                  <div className="text-xs text-muted-foreground font-mono">{p.sku}</div>
                </Td>
                <Td mono className={dimClass(v)}>{v.is_digital ? "—" : dimStr(v)}</Td>
                <Td className={v.supplier_id && supMap[v.supplier_id] ? "" : "text-muted-foreground"}>
                  {(v.supplier_id && supMap[v.supplier_id]) || "—"}</Td>
                <Td mono className={v.ean ? "" : "text-chart-3"}>{v.ean || t("common.missing")}</Td>
                <Td mono className={v.hs_code ? "" : "text-muted-foreground"}>{v.hs_code || "—"}</Td>
                {canCost && <Td right mono>{eur(v.cost_price)}</Td>}
                <Td right mono>{eur(v.price_net)}</Td>
                <Td right mono>{eur((v.price_net || 0) * (1 + (v.tax_rate ?? 19) / 100))}
                  <span className="ml-1 text-xs text-muted-foreground">({v.tax_rate ?? 19}%)</span></Td>
                <Td right mono title={t("prod.secondChoice")} data-testid={`second-net-${p.sku}`}>
                  {v.price_net_second != null ? eur(v.price_net_second) : "—"}</Td>
                <Td right mono title={t("prod.secondChoice")} data-testid={`second-gross-${p.sku}`}>
                  {v.price_net_second != null
                    ? eur(v.price_gross_second ?? (v.price_net_second * (1 + (v.tax_rate ?? 19) / 100)))
                    : "—"}</Td>
                <Td right>{canEdit && (
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="outline" onClick={() => onEdit(p)} data-testid={`edit-${p.sku}`}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant={p.archived ? "default" : "ghost"} data-testid={`arch-${p.sku}`}
                      title={p.archived ? t("common.unarchive") : t("common.archive")} onClick={() => onArchive(p)}>
                      <Archive className="h-4 w-4" />{p.archived && <span className="ml-1.5 text-xs">{t("common.unarchive")}</span>}
                    </Button>
                    <Button size="sm" variant="ghost" disabled={p.has_sales} data-testid={`del-${p.sku}`}
                      title={p.has_sales ? t("prod.hasSalesHint") : t("common.delete")} onClick={() => onDelete(p)}>
                      <Trash2 className={`h-4 w-4 ${p.has_sales ? "text-muted-foreground/40" : "text-destructive"}`} />
                    </Button>
                  </div>)}</Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
